import { trackAnalyticEvent } from "../../util/analytics"

export type ToolId =
  | "bcba_goal_generator"
  | "task_analysis_builder"
  | "goal_writing_assistant"
  | "intervention_strategy_generator"
  | "progress_note_template_creator"
  | "bip_template_generator"
  | "session_note_summarizer"
  | "aba_assessment_template_generator"
  | "data_collection_helper"

export type ToolCategory = "Skill Generalization" | "Parent Training"

export type ToolMeta = {
  id: ToolId
  name: string
  description: string
  category: ToolCategory
  image: string
  path: string
}

export const TOOLS: ToolMeta[] = [
  {
    id: "intervention_strategy_generator",
    name: "Intervention Strategy Generator",
    description:
      "Need helping coming up with interventions based on your client's target behaviors? Try this free tool to get some ideas!",
    category: "Skill Generalization",
    image: "/images/tools/intervention-strategy-generator.jpg",
    path: "/tools/intervention-strategy-generator",
  },
  {
    id: "session_note_summarizer",
    name: "Session Note Summarizer",
    description:
      "Need helping formatting or summarizing your session notes during ABA parent training? Try this free tool to get some formatting help!",
    category: "Parent Training",
    image: "/images/tools/session-note-summarizer.jpg",
    path: "/tools/session-note-summarizer",
  },
  {
    id: "task_analysis_builder",
    name: "Task Analysis Builder",
    description:
      "Need helping breaking down tasks or skills for a learner during ABA treatment? Try this free tool to get some task analysis suggestions.",
    category: "Skill Generalization",
    image: "/images/tools/task-analysis-builder.png",
    path: "/tools/task-analysis-builder",
  },
  {
    id: "bip_template_generator",
    name: "BIP Template Generator",
    description:
      "Need helping coming up with BIP templates based on your client? Try this free tool to get some ideas!",
    category: "Skill Generalization",
    image: "/images/tools/bip-template-generator.png",
    path: "/tools/bip-template-generator",
  },
  {
    id: "goal_writing_assistant",
    name: "Client Goal Generator",
    description:
      "Need helping coming up with ideas for client goals and mastery criteria during ABA? Try this free tool to get some ideas!",
    category: "Skill Generalization",
    image: "/images/tools/goal-writing-assistant.jpg",
    path: "/tools/goal-writing-assistant",
  },
  {
    id: "progress_note_template_creator",
    name: "Progress Note Templates",
    description:
      "Need helping coming up with templates for documenting progress notes for clients during ABA? Try this free tool to get some templates!",
    category: "Skill Generalization",
    image: "/images/tools/progress-note-template-creator.jpg",
    path: "/tools/progress-note-template-creator",
  },
  {
    id: "bcba_goal_generator",
    name: "Parent Goal Generator",
    description:
      "Need helping coming up with ideas for parent goals and mastery criteria during ABA parent training? Try this free tool to get some ideas!",
    category: "Parent Training",
    image: "/images/tools/bcba-goal-generator.jpg",
    path: "/tools/bcba-goal-generator",
  },
  {
    id: "data_collection_helper",
    name: "Data Collection Helper",
    description:
      "Need help figuring out how to collect the data you need during ABA parent training? Try this free tool to get some ideas!",
    category: "Parent Training",
    image: "/images/tools/data-collection-helper.png",
    path: "/tools/data-collection-helper",
  },
  {
    id: "aba_assessment_template_generator",
    name: "ABA Assessment Template Generator",
    description:
      "Need help drafting an insurance-ready ABA assessment template? Try this free tool to generate one tailored to your client.",
    category: "Skill Generalization",
    image: "/images/tools/aba-assessment-template-generator.jpg",
    path: "/tools/aba-assessment-template-generator",
  },
]

export const CATEGORY_COLORS: Record<
  ToolCategory,
  { background: string; text: string }
> = {
  "Skill Generalization": { background: "#F59E5C", text: "#FFFFFF" },
  "Parent Training": { background: "#1E2148", text: "#FFFFFF" },
}

const toolName = (id: ToolId) =>
  TOOLS.find((t) => t.id === id)?.name ?? id

export function trackPageViewed(toolId: ToolId) {
  trackAnalyticEvent("tool:page_viewed", {
    tool_id: toolId,
    tool_name: toolName(toolId),
    path: typeof window !== "undefined" ? window.location.pathname : null,
  })
}

export function trackGenerationStarted(
  toolId: ToolId,
  props: Record<string, any> = {}
) {
  trackAnalyticEvent("tool:generation_started", {
    tool_id: toolId,
    tool_name: toolName(toolId),
    ...props,
  })
}

export function trackGenerationCompleted(
  toolId: ToolId,
  props: Record<string, any> = {}
) {
  trackAnalyticEvent("tool:generation_completed", {
    tool_id: toolId,
    tool_name: toolName(toolId),
    ...props,
  })
}

export function trackGenerationFailed(
  toolId: ToolId,
  props: Record<string, any> = {}
) {
  trackAnalyticEvent("tool:generation_failed", {
    tool_id: toolId,
    tool_name: toolName(toolId),
    ...props,
  })
}

export function trackResultCopied(
  toolId: ToolId,
  props: Record<string, any> = {}
) {
  trackAnalyticEvent("tool:result_copied", {
    tool_id: toolId,
    tool_name: toolName(toolId),
    ...props,
  })
}

export async function copyText(text: string) {
  await navigator.clipboard.writeText(text)
}
